import {CollapsibleNavList} from '@github-ui/layout-primitives/CollapsibleNavList'
import {Link} from '@github-ui/react-router'
import {getIconColorClassName, getIconComponent} from '@github-ui/saved-views'
import {PinIcon} from '@primer/octicons-react'
import {NavList} from '@primer/react'
import type {ComponentProps} from 'react'

import styles from './PinnedViewsNav.module.css'

export interface SidebarPinnedView {
  id: number
  name: string
  icon: string
  color: string
}

const PINNED_GROUP_LABEL = 'Pinned'
const PINNED_NAV_LABEL = 'Pinned views'

export interface PinnedViewsNavProps {
  pinnedViews: SidebarPinnedView[]
  viewsPath: string
  pathname: string
  forceHardNavigation: boolean
  collapsible: boolean
  onSelectView?: (id: number) => void
}

export function PinnedViewsNav({
  pinnedViews,
  viewsPath,
  pathname,
  forceHardNavigation,
  collapsible,
  onSelectView,
}: PinnedViewsNavProps) {
  if (pinnedViews.length === 0) return null

  const items = pinnedViews.map(view => {
    const to = `${viewsPath}/${view.id}`
    return (
      <PinnedViewItem
        key={view.id}
        view={view}
        to={to}
        aria-current={pathname === to ? 'page' : undefined}
        forceHardNavigation={forceHardNavigation}
        onClick={() => onSelectView?.(view.id)}
      />
    )
  })

  if (collapsible) {
    return (
      <>
        <hr className={styles.dividerCollapsible} />
        <CollapsibleNavList aria-label={PINNED_NAV_LABEL} className={styles.pinnedNav}>
          <CollapsibleNavList.Group icon={PinIcon} title={PINNED_GROUP_LABEL}>
            {items}
          </CollapsibleNavList.Group>
        </CollapsibleNavList>
      </>
    )
  }

  return (
    <>
      <hr className={styles.divider} />
      <NavList aria-label={PINNED_NAV_LABEL} className={styles.pinnedNav}>
        <NavList.Group title={PINNED_GROUP_LABEL}>{items}</NavList.Group>
      </NavList>
    </>
  )
}

function PinnedViewItem({
  view,
  to,
  'aria-current': ariaCurrent,
  forceHardNavigation,
  onClick,
}: {
  view: SidebarPinnedView
  to: string
  'aria-current'?: 'page'
  forceHardNavigation: boolean
  onClick?: () => void
}) {
  const linkProps = (forceHardNavigation ? {href: to} : {as: Link, to}) as ComponentProps<typeof NavList.Item>

  return (
    <NavList.Item {...linkProps} aria-current={ariaCurrent} onClick={onClick}>
      <NavList.LeadingVisual>
        <PinnedViewIcon icon={getIconComponent(view.icon)} color={view.color} />
      </NavList.LeadingVisual>
      {view.name}
    </NavList.Item>
  )
}

function PinnedViewIcon({icon: Icon, color}: {icon: ReturnType<typeof getIconComponent>; color: string}) {
  return <Icon size={16} className={getIconColorClassName(color)} />
}
